import { FormSection } from "components/MainSectionTemplate/types";
import ContactModalPicker from "components/Picker/ContactPicker/ContactModalPicker";
import PromoterModalPicker from "components/Picker/PromoterPicker/PromoterModalPicker";
import { useModals } from "Modals/ModalsProvider";
import React from "react";
import ReadonlyCompany from "show/detail/components/ReadonlyCompany";
import ReadonlyContact from "show/detail/components/ReadonlyContact";
import { fetchFullContact, getInstructionForInternal, ShowInfo } from "show/detail/utils";
// Wrapper component so useModals is called at the component level (not inside a render callback)
const PromoterPickerWithRisk = (props: {
  onChange: (value: server.dto.CompanySmall, submit: boolean) => void
  cancel: () => void
  hasValue: boolean,
  isSidebar: boolean
}) => {
  const modals = useModals();
  return (
    <div className="main-section-template-fields-padding">
      <PromoterModalPicker
        inPageEditing={{
          onChange: (v, submit) => {
            if (v) {
              modals.displayFinancialRiskModal(v.companyId);
            }
            props.onChange(v, submit);
          },
          cancel: props.cancel,
          hasValue: props.hasValue,
        }}
        canCreateNew={!props.isSidebar && site.permissions.addressBook.canCreate}
        afterCreate={props.isSidebar ? "goBack" : "closeSidebar"}
      />
    </div>
  );
};

export const promoterSection = (
  show: server.dto.Show,
  showInfo: ShowInfo,
  promoterContact: server.dto.Contact,
  isExternal: boolean,
  isSidebar: boolean
): FormSection => {
  const isInternalUser = !isExternal;

  const section: FormSection = {
    type: "regular",
    key: "showPromoterSection",
    title: site.currentEnvironment.functionGroups.promoter.singular,
    hidden: !site.currentEnvironment.functionGroups.promoter.enabled,
    fields: [{
      key: "promoter",
      label: site.currentEnvironment.functionGroups.promoter.singular,
      isEmpty: !showInfo.promoter,
      render: () => <ReadonlyCompany company={showInfo.promoter} displayAs="promoter" displayTooltip={isInternalUser} />,
      inPageEdit: {
        hoverEdit: true,
        canRemove: true,
        initialValue: showInfo.promoter,
        fullWidth: true,
        render: ({ onChange, cancel, value }) => (
          <PromoterPickerWithRisk onChange={onChange} cancel={cancel} hasValue={Boolean(value)} isSidebar={isSidebar} />
        ),
        type: "custom",
        submitChange: (value: server.dto.CompanySmall) =>
          window.CoreApi.Show.UpdateForInternalEnvironment(
            getInstructionForInternal(show.showId, showInfo, {
              promoterCompanyId: value?.companyId ?? null,
              promoterContactId: null,
            })
          )
      }
    }, {
      key: "promoterContact",
      hidden: showInfo.promoter == null,
      label: RESX.Contact.resxContact,
      isEmpty: !promoterContact,
      render: () => <ReadonlyContact contact={promoterContact} displayDetails={show.permissions.canReadPromoterDetails} displayPreview={isInternalUser} />,
      inPageEdit: {
        initialValue: promoterContact,
        fullWidth: true,
        hoverEdit: true,
        canRemove: true,
        render: ({ onChange, cancel, value }) => (
          <div className="main-section-template-fields-padding">
            <ContactModalPicker
              inPageEditing={{
                onChange: async (picked: server.dto.ContactSmallV2, submit: boolean) => {
                  const full = await fetchFullContact(picked);
                  onChange(full, submit);
                },
                cancel,
                hasValue: Boolean(value),
              }}
              fromCompanyId={showInfo.promoter?.companyId}
              canCreateNew={!isSidebar && site.permissions.addressBook.canCreate}
              afterCreate={isSidebar ? "goBack" : "closeSidebar"}
            />
          </div>
        ),
        type: "custom",
        submitChange: (value: server.dto.Contact) =>
          window.CoreApi.Show.UpdateForInternalEnvironment(
            getInstructionForInternal(show.showId, showInfo, { promoterContactId: value?.contactId ?? null })
          )
      }
    }]
  };

  if (isExternal) {
    section.fields = section.fields.map(f => ({ ...f, editDisabled: true }));
  }
  return section;
};
